/** The time window of Explore and Compare: presets and days, in station
 *  time (Review focus 3). Everything goes through lib/time.ts; a browser in
 *  another zone gets the same instants. */
import { STATION_TZ, formatDate, fromLocalInput } from './time'

/** '1y' is offered nowhere now; a saved comparison view may hold it (lib/carryOver.ts). */
export type Preset = '24h' | '7d' | '30d' | '1y'

export const PRESET_LABEL: Record<Preset, string> = {
  '24h': '24 h', '7d': '7 d', '30d': '30 d', '1y': '1 y',
}

const DAY_MS = 86_400_000
const PRESET_DAYS: Record<Exclude<Preset, '24h'>, number> = { '7d': 7, '30d': 30, '1y': 365 }

/** Local midnight at the start of `day` (YYYY-MM-DD) in `tz`. */
function midnight(day: string, tz: string): Date {
  return fromLocalInput(`${day}T00:00`, tz)
}

/** The day after `day`, as YYYY-MM-DD. Calendar arithmetic on the date
 *  alone, so no zone and no DST can move it. */
function nextDay(day: string): string {
  return new Date(Date.parse(`${day}T00:00:00Z`) + DAY_MS).toISOString().slice(0, 10)
}

/** Every preset ends now. "24 h" is exactly that; the day presets start at
 *  the station-local midnight that many days back, a boundary a reader
 *  recognises, as the 7-day default always has. */
export function presetRange(preset: Preset, now: Date, tz = STATION_TZ): { from: Date; to: Date } {
  if (preset === '24h') return { from: new Date(now.getTime() - DAY_MS), to: now }
  const day = formatDate(new Date(now.getTime() - PRESET_DAYS[preset] * DAY_MS), tz)
  return { from: midnight(day, tz), to: now }
}

/** A Custom range of whole days, `YYYY-MM-DD` in station time, in either
 *  order: from local midnight of the first to local midnight after the
 *  last, the end capped at now so a range reaching today asks for nothing
 *  that has not happened. */
export function dayRange(firstDay: string, lastDay: string, tz = STATION_TZ,
                         now: Date = new Date()): { from: Date; to: Date } {
  const [lo, hi] = firstDay <= lastDay ? [firstDay, lastDay] : [lastDay, firstDay]
  const end = midnight(nextDay(hi), tz)
  return { from: midnight(lo, tz), to: end > now ? now : end }
}

/** One month as weeks of seven, Monday first, blank (`null`) outside the
 *  month. `month` is 1 to 12. */
export function monthGrid(year: number, month: number): (string | null)[][] {
  const first = new Date(Date.UTC(year, month - 1, 1))
  const days = new Date(Date.UTC(year, month, 0)).getUTCDate()
  const lead = (first.getUTCDay() + 6) % 7
  const cells: (string | null)[] = Array.from({ length: lead }, () => null)
  for (let d = 1; d <= days; d += 1) {
    cells.push(`${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`)
  }
  while (cells.length % 7) cells.push(null)
  const weeks: (string | null)[][] = []
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7))
  return weeks
}

interface Rect { left: number; top: number; bottom: number; right: number }
interface Size { width: number; height: number }

const GAP = 4
const EDGE = 8

/** Where a fixed popover goes beside its anchor: below it, or above when
 *  there is no room below, never past the window's edge. Measurements come
 *  back in screen pixels and the page may be zoomed (styles.css), so the
 *  answer is divided by the zoom, the rule StationPicker's list follows. */
export function placePopover(anchor: Rect, size: Size, viewport: Size,
                             zoom: number): { left: number; top: number } {
  const below = viewport.height - anchor.bottom - GAP - EDGE
  const fitsBelow = below >= size.height || anchor.top - GAP - EDGE < size.height
  const rawTop = fitsBelow ? anchor.bottom + GAP : anchor.top - GAP - size.height
  const top = Math.max(EDGE, Math.min(rawTop, viewport.height - EDGE - size.height))
  const left = Math.min(Math.max(anchor.left, EDGE), viewport.width - EDGE - size.width)
  return { left: Math.max(EDGE, left) / zoom, top: top / zoom }
}

/** What the time window holds. A preset keeps the instant it was chosen,
 *  so its range does not move under a chart on every render; choosing it
 *  again brings it up to now. A custom window is the two wall-clock strings
 *  of the exact-time fields, in station time, kept as text so a half-typed
 *  date does not blank the chart; the calendar fills them with whole days. */
export type WindowValue =
  | { kind: 'preset'; preset: Preset; at: number }
  | { kind: 'custom'; fromText: string; toText: string }

/** How long a preset's window may stand still and still be called Live. A
 *  preset keeps the instant it was chosen, so the chart does not move under
 *  the reader; past this the window is visibly not "now" any more and the
 *  badge goes, rather than say something the chart no longer is. */
export const LIVE_FOR_MS = 5 * 60_000

export function isLiveWindow(value: WindowValue, zoomed: boolean, now: Date): boolean {
  return value.kind === 'preset' && !zoomed && now.getTime() - value.at <= LIVE_FOR_MS
}

export function windowRange(value: WindowValue, tz = STATION_TZ): { from: Date; to: Date; valid: boolean } {
  if (value.kind === 'preset') return { ...presetRange(value.preset, new Date(value.at), tz), valid: true }
  const from = fromLocalInput(value.fromText, tz)
  const to = fromLocalInput(value.toText, tz)
  const valid = !Number.isNaN(from.getTime()) && !Number.isNaN(to.getTime()) && from < to
  return { from, to, valid }
}
